import { CreateBundleProductCard } from '@/components/catalogue/products/cards/CreateBundleProductCard';
import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Counter } from '@/components/form/fields/number/Counter';
import { TextField } from '@/components/form/fields/text/TextInput';
import { cn } from '@/lib/utils';
import { Bundle } from '@/services/api/contracts/catalog/bundles';
import { Product } from '@/services/api/contracts/catalog/types';
import { useIsBundlesMissingFinanceModels } from '@/services/bundles/useIsBundlesMissingFinanceModels';
import { useSelectedBundleProducts } from '@/services/bundles/useSelectedBundleProducts';
import { useCustomerRoutePrefix } from '@/services/customer/useCustomerRoutePrefix';
import { usePrintCurrencyStringForCurrentCustomer } from '@/utils/price';
import { zodResolver } from '@hookform/resolvers/zod';
import { FunctionComponent, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';

const CreateEditBundleFormFields = z.object({
  name: z.string().min(1, 'Required')
});

export type CreateEditBundleFormFields = z.infer<typeof CreateEditBundleFormFields>;

interface MappedProduct extends Product {
  count: number;
}

export type CreateEditBundleSubmitData = {
  name: string;
  peripherals: MappedProduct[];
};

interface CreateEditBundleFormProps {
  bundle?: Bundle;
  onSubmitCallback: (data: CreateEditBundleSubmitData) => Promise<void>;
}

const CreateEditBundleForm: FunctionComponent<CreateEditBundleFormProps> = ({ bundle, onSubmitCallback }) => {
  const navigate = useNavigate();

  const methods = useForm<CreateEditBundleFormFields>({
    resolver: zodResolver(CreateEditBundleFormFields),
    defaultValues: {
      name: bundle?.name ?? ''
    }
  });

  const { mainDeviceProducts, peripheralProducts } = useSelectedBundleProducts();

  const [mappedProducts, setMappedProducts] = useState<MappedProduct[]>(
    [...mainDeviceProducts, ...peripheralProducts].map(item => {
      const count = bundle?.bundledItems.find(product => product.catalogItem === item.id)?.count;
      // If it was exactly the mainDevice, it might not be in bundledItems with a count if the DB saved it without count. 
      // But we will give it a count of 1 if it's the main device for backwards compatibility
      const mainDeviceCount = item.id === bundle?.mainDevice ? 1 : 0;
      return {
        ...item,
        count: count ?? mainDeviceCount
      };
    })
  );

  const selectedProducts = useMemo(
    () => mappedProducts.filter(product => product.count > 0),
    [mappedProducts]
  );

  const totalPrice = useMemo(() => {
    return selectedProducts.reduce(
      (total, currentItem) => total + currentItem.count * (currentItem.price?.price ?? 0),
      0
    );
  }, [selectedProducts]);

  const [loading, setLoading] = useState(false);

  const onSubmit = async (data: CreateEditBundleFormFields) => {
    setLoading(true);
    await onSubmitCallback({
      name: data.name,
      peripherals: selectedProducts
    });
    setLoading(false);
  };

  const isBundleMissingFinanceModels = useIsBundlesMissingFinanceModels(bundle ? [bundle] : []);

  const printCurrencyString = usePrintCurrencyStringForCurrentCustomer();

  const routePrefix = useCustomerRoutePrefix();

  return (
    <div className="overflow-y-auto px-6 py-8">
      {mappedProducts.length === 0 ? (
        <div className="space-y-5 pt-5">
          <p>Your standardised product list does not contain any items.</p>
          <p>Please return to the catalogue and select at least one item.</p>
          <Button variant={ButtonVariant.Primary} onClick={() => navigate(`${routePrefix}/catalogue`)}>
            Go To Catalogue
          </Button>
        </div>
      ) : (
        <>
          <p className={cn('text-2xl font-bold', isBundleMissingFinanceModels && 'mt-5')}>Bundle Details</p>

          <Form className="flex w-full flex-col py-3 space-y-10" methods={methods} onSubmit={onSubmit}>
            <TextField id={`name`} label="Bundle Name" example="Example: Developers, Communications, Off site staff" />

            <div className="space-y-1">
              <p className="text-2xl font-bold">Products</p>
              <p>Select any number of products for the bundle.</p>

              <div className="grid h-fit grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
                {mappedProducts.map(product => (
                  <CreateBundleProductCard key={product.id} product={product} selected={product.count > 0}>
                    <div className="flex justify-center py-2">
                      <Counter
                        id={product.id}
                        value={product.count}
                        onChange={value => {
                          product.count = value;
                          setMappedProducts([...mappedProducts]);
                        }}
                      />
                    </div>
                  </CreateBundleProductCard>
                ))}
              </div>
            </div>

            <div className="w-[26rem]">
              <p className="text-2xl font-bold">Bundle Cost</p>

              <div className="mt-2 text-sm font-semibold">
                {selectedProducts.length > 0 &&
                  selectedProducts.map(product => (
                    <div key={`cost-${product.id}`} className="space-x-2">
                      <span>
                        {product?.name}
                        {product.count > 1 ? ` X ${product.count}` : ''}:
                      </span>
                      <span className="text-right font-semibold">
                        {printCurrencyString(product.count * (product?.price?.price ?? 0))}
                      </span>
                    </div>
                  ))}
              </div>

              <div className="pt-1 mt-2 border-t-2 border-black text-xl font-bold space-x-2">
                <span className="border-black">Total: </span>
                <span className="text-right font-semibold">{printCurrencyString(totalPrice)}</span>
              </div>
            </div>

            <div className="flex space-x-3 pt-2">
              <Button
                className="w-fit"
                variant={ButtonVariant.Mono}
                type="button"
                onClick={() => {
                  navigate(-1);
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={selectedProducts.length === 0}
                loading={loading}
                className="w-fit"
                variant={ButtonVariant.Primary}
                type="submit"
              >
                {bundle ? 'Save Bundle' : 'Create Bundle'}
              </Button>
            </div>
          </Form>
        </>
      )}
    </div>
  );
};

export default CreateEditBundleForm;
